/**
 * @file columnResolver.ts
 * @description Pure functions that resolve column visibility and editability
 * based on role, workflow step, permissions, entity status, and program overrides.
 *
 * GOVERNED BY: copilot-instructions.md – Dynamic workflow permission model
 */

import type {
  DataGridColumn,
  ColumnVisibilityRules,
  ColumnResolverContext,
} from './dataGridTypes';

// ─────────────────────────────────────────────────────────────
// Program Column Override (from DB)
// ─────────────────────────────────────────────────────────────

export interface ProgramColumnOverride {
  /** The column settingsKey this override applies to */
  columnKey: string;
  /** Whether the column is visible (null = use default) */
  isVisible?: boolean | null;
  /** Whether the column is editable (null = use default) */
  isEditable?: boolean | null;
  /** Display order override (null = use default) */
  displayOrder?: number | null;
  /** Custom label override */
  labelOverride?: string | null;
}

// ─────────────────────────────────────────────────────────────
// Visibility Check
// ─────────────────────────────────────────────────────────────

/**
 * Evaluates whether a column should be visible given the current context.
 * Returns false if ANY rule set fails (AND logic between rule types).
 * Within each rule array, OR logic applies (user needs at least one match).
 */
function isColumnVisible<TRow>(
  rules: ColumnVisibilityRules<TRow> | undefined,
  context: ColumnResolverContext,
): boolean {
  if (!rules) return true; // No rules = always visible
  if (context.isSuperAdmin) return true; // SuperAdmin bypass

  // Role check: user must have one of the listed roles
  if (rules.roles?.length) {
    const match = rules.roles.some(
      (r) => r.toLowerCase() === context.userRole.toLowerCase()
        || r.toLowerCase() === context.roleCategory?.toLowerCase(),
    );
    if (!match) return false;
  }

  // Workflow step check: entity must be at one of the listed steps
  if (rules.workflowSteps?.length) {
    if (!context.workflowStep) return false;
    const match = rules.workflowSteps.some(
      (s) => s.toLowerCase() === context.workflowStep!.toLowerCase(),
    );
    if (!match) return false;
  }

  // Permission check: user must have one of the listed permissions
  if (rules.permissions?.length) {
    const match = rules.permissions.some((p) => context.permissions.has(p));
    if (!match) return false;
  }

  // Status check: entity must be in one of the listed statuses
  if (rules.statuses?.length) {
    if (!context.entityStatus) return false;
    const match = rules.statuses.some(
      (s) => s.toLowerCase() === context.entityStatus!.toLowerCase(),
    );
    if (!match) return false;
  }

  // Condition check (static boolean or function — function evaluated without row context here)
  if (rules.condition !== undefined) {
    if (typeof rules.condition === 'boolean') {
      if (!rules.condition) return false;
    }
    // Function conditions are evaluated per-row in resolveColumnsForRow
  }

  return true;
}

// ─────────────────────────────────────────────────────────────
// Editability Check
// ─────────────────────────────────────────────────────────────

/**
 * Determines if a specific column should be editable given the current context.
 * If no editabilityRules are specified, falls back to the column's `editable` boolean.
 */
export function isColumnEditable<TRow>(
  column: DataGridColumn<TRow>,
  context: ColumnResolverContext,
): boolean {
  // If column has no editability rules, use the simple boolean
  if (!column.editabilityRules) {
    return column.editable ?? false;
  }

  if (context.isSuperAdmin) return column.editable !== false;

  const rules = column.editabilityRules;

  // Role check
  if (rules.roles?.length) {
    const match = rules.roles.some(
      (r) => r.toLowerCase() === context.userRole.toLowerCase()
        || r.toLowerCase() === context.roleCategory?.toLowerCase(),
    );
    if (!match) return false;
  }

  // Workflow step check
  if (rules.workflowSteps?.length) {
    if (!context.workflowStep) return false;
    const match = rules.workflowSteps.some(
      (s) => s.toLowerCase() === context.workflowStep!.toLowerCase(),
    );
    if (!match) return false;
  }

  // Permission check
  if (rules.permissions?.length) {
    const match = rules.permissions.some((p) => context.permissions.has(p));
    if (!match) return false;
  }

  // Status check
  if (rules.statuses?.length) {
    if (!context.entityStatus) return false;
    const match = rules.statuses.some(
      (s) => s.toLowerCase() === context.entityStatus!.toLowerCase(),
    );
    if (!match) return false;
  }

  // Condition
  if (rules.condition !== undefined) {
    if (typeof rules.condition === 'boolean') return rules.condition;
  }

  return true;
}

// ─────────────────────────────────────────────────────────────
// Main Resolver
// ─────────────────────────────────────────────────────────────

export interface ResolveColumnsOptions {
  /** Program-level overrides from DB (ProgramColumnSettings) */
  programOverrides?: ProgramColumnOverride[];
  /** User column preference (hidden columns) — keys the user has explicitly hidden */
  userHiddenColumns?: Set<string>;
}

/**
 * Resolves the final set of visible columns after applying:
 * 1. Column-level visibilityRules (role, workflow, permissions, status)
 * 2. Program-level overrides (ProgramColumnSettings from DB)
 * 3. User preferences (hidden columns)
 *
 * Also enriches each column with resolved editability.
 *
 * @returns Filtered and enriched column array ready for DataGrid rendering.
 */
export function resolveColumns<TRow>(
  columns: DataGridColumn<TRow>[],
  context: ColumnResolverContext,
  options: ResolveColumnsOptions = {},
): DataGridColumn<TRow>[] {
  const { programOverrides, userHiddenColumns } = options;

  // Build override lookup
  const overrideMap = new Map<string, ProgramColumnOverride>();
  if (programOverrides) {
    for (const override of programOverrides) {
      overrideMap.set(override.columnKey, override);
    }
  }

  const resolved: DataGridColumn<TRow>[] = [];

  for (const col of columns) {
    const key = String(col.key);
    const settingsKey = col.settingsKey ?? key;
    const override = overrideMap.get(settingsKey);

    // 1. Program override can force-hide a column
    if (override?.isVisible === false) continue;

    // 2. Column-level visibilityRules
    if (col.visible === false) continue;
    if (!isColumnVisible(col.visibilityRules, context)) continue;

    // 3. User preference — user has hidden this column
    if (userHiddenColumns?.has(key)) continue;

    // Build resolved column
    const resolvedCol: DataGridColumn<TRow> = { ...col };

    // Apply label override from program settings
    if (override?.labelOverride) {
      resolvedCol.label = override.labelOverride;
    }

    // Resolve editability
    if (override?.isEditable === false) {
      resolvedCol.editable = false;
    } else if (override?.isEditable === true) {
      resolvedCol.editable = isColumnEditable(col, context);
    } else {
      resolvedCol.editable = isColumnEditable(col, context);
    }

    resolved.push(resolvedCol);
  }

  // Apply display order overrides
  if (programOverrides?.some((o) => o.displayOrder != null)) {
    resolved.sort((a, b) => {
      const aKey = a.settingsKey ?? String(a.key);
      const bKey = b.settingsKey ?? String(b.key);
      const aOrder = overrideMap.get(aKey)?.displayOrder;
      const bOrder = overrideMap.get(bKey)?.displayOrder;
      if (aOrder != null && bOrder != null) return aOrder - bOrder;
      if (aOrder != null) return -1;
      if (bOrder != null) return 1;
      return 0; // preserve original order
    });
  }

  return resolved;
}
